import { useEffect, useState } from "react";
import { RotateCcw, Info } from "lucide-react";
import { toast } from "./ui/sonner";
import { resetDemo } from "@/services/demo/store";
import { clearApiGetCache } from "@/api";

// Top banner shown only in demo mode. Makes the "sample data only" state obvious
// and offers an in-SPA reset (no full page reload). This is an additive, demo-only
// conditional in shared code — it does not duplicate any page or layout.
export default function DemoBanner() {
  const [resetting, setResetting] = useState(false);

  // Push the app down so the fixed banner never covers the real top nav.
  useEffect(() => {
    const prev = document.body.style.paddingTop;
    document.body.style.paddingTop = "40px";
    document.body.classList.add("jf-demo-mode");
    return () => {
      document.body.style.paddingTop = prev;
      document.body.classList.remove("jf-demo-mode");
    };
  }, []);

  const handleReset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      await resetDemo();
      clearApiGetCache();
      window.dispatchEvent(new CustomEvent("jf-demo-reset"));
      toast.success("Demo reset — fresh sample data restored.");
    } catch {
      toast.error("Could not reset the demo. Please refresh the page.");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      className="fixed inset-x-0 top-0 z-[9999] flex h-10 items-center gap-3 bg-emerald-700 px-3 text-white shadow-md sm:px-4"
      role="region"
      aria-label="Demo mode notice"
    >
      <Info className="h-4 w-4 shrink-0 text-emerald-100" aria-hidden="true" />
      <p className="min-w-0 flex-1 truncate text-[12px] sm:text-sm">
        <span className="font-semibold">Demo mode</span>
        <span className="text-emerald-100"> — sample data only. Nothing is saved to a real business.</span>
      </p>
      <button
        type="button"
        onClick={handleReset}
        disabled={resetting}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-white/15 px-2.5 py-1 text-[12px] font-medium transition hover:bg-white/25 disabled:opacity-60 sm:text-sm"
      >
        <RotateCcw className={`h-3.5 w-3.5 ${resetting ? "animate-spin" : ""}`} aria-hidden="true" />
        {resetting ? "Resetting…" : "Reset demo"}
      </button>
    </div>
  );
}
